import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';

import { usePrevious } from '../use-previous';

const getFromStorage = <S>(
  storage: Storage,
  key: string,
  getInitialValue: () => S,
): S => {
  const item = storage.getItem(key);

  if (!item) return getInitialValue();

  try {
    return JSON.parse(item);
  } catch (error) {
    return getInitialValue();
  }
};

export const useStorage = <S>(
  key: string,
  initialValue: S | (() => S),
  storage: Storage = window.localStorage,
): [S, Dispatch<SetStateAction<S>>] => {
  const previousKey = usePrevious(key);
  const initialValueRef = useRef(initialValue);
  const storageRef = useRef(storage);
  const getInitialValue = useCallback((): S => {
    // This will make it so we only run `initialValue` once if it is a function
    if (typeof initialValueRef.current === 'function') {
      initialValueRef.current = (initialValueRef.current as () => S)();
    }

    return initialValueRef.current;
  }, []);
  const updated = useRef(false);
  const [storedValue, setStoredValue] = useState<S>(() =>
    getFromStorage(storageRef.current, key, getInitialValue),
  );

  // istanbul ignore next: hard to test and probably not worth it
  const handleStorageEvent = useCallback(
    ({ key: eventKey, newValue }: StorageEvent): void => {
      if (eventKey !== key) return;

      if (updated.current) {
        updated.current = false;

        return;
      }

      setStoredValue(prevValue => {
        try {
          const nextValue = newValue !== null ? JSON.parse(newValue) : null;
          const valueToStore =
            nextValue === null ? getInitialValue() : nextValue;

          return isEqual(valueToStore, prevValue) ? prevValue : valueToStore;
        } catch (error) {
          return prevValue;
        }
      });
    },
    [getInitialValue, key],
  );

  useEffect(() => {
    if (!previousKey || previousKey === key) return;

    setStoredValue(getFromStorage(storageRef.current, key, getInitialValue));
  }, [getInitialValue, key, previousKey]);

  useEffect(() => {
    window.addEventListener('storage', handleStorageEvent);

    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [handleStorageEvent]);

  const setValue = useCallback(
    (value: SetStateAction<S>): void => {
      setStoredValue(prevValue => {
        let hasNextValue = false;
        let valueToStore = prevValue;

        try {
          const nextValue =
            value instanceof Function ? value(prevValue) : value;

          valueToStore =
            nextValue === null
              ? /* istanbul ignore next: TS does not allow this */ getInitialValue()
              : nextValue;
          hasNextValue = true;

          const newValue = JSON.stringify(valueToStore);

          storageRef.current.setItem(key, newValue);
          updated.current = true;
          window.requestAnimationFrame(() => {
            try {
              // If we have the same key used multiple times, this makes sure
              // each one gets the update
              window.dispatchEvent(
                new StorageEvent('storage', {
                  key,
                  newValue,
                  oldValue: JSON.stringify(prevValue),
                }),
              );
            } catch (error) {
              updated.current = false;
            }
          });

          return valueToStore;
        } catch (error) {
          updated.current = false;

          if (hasNextValue) {
            const persistedValue = getFromStorage(
              storageRef.current,
              key,
              getInitialValue,
            );

            return isEqual(persistedValue, valueToStore)
              ? valueToStore
              : prevValue;
          }

          return prevValue;
        }
      });
    },
    [getInitialValue, key],
  );

  return [storedValue, setValue];
};
